/**
 * Minimal header (Angular 18)
 *
 * The minimal header mode — a single "Close" link inside the shared header
 * chrome. Composes {@link HeaderShellComponent} directly via standalone
 * imports. Has no public inputs/outputs: it derives the shell's state
 * (`isLoggedIn`, `useCart`, `companyName`) from the already-Angular
 * AuthenticationService / FeatureToggleService / ThemesHelperService, exactly
 * as the AngularJS `header` controller did.
 *
 * Migrated from apps/b2b-ui/common/directives/header/minimal.tpl.html.
 * Downgraded for AngularJS as `<ng-header-minimal>`.
 */
import { Component, inject } from '@angular/core';
import { NgIf } from '@angular/common';

import { AuthenticationService } from '../../../core/auth/authentication.service';
import { FeatureToggleService } from '../../../core/feature-toggle/feature-toggle.service';
import { ThemesHelperService } from '../../../core/themes-helper/themes-helper.service';
import { HeaderShellComponent } from '../header-shell/header-shell.component';

@Component({
  selector: 'app-header-minimal',
  standalone: true,
  imports: [NgIf, HeaderShellComponent],
  template: require('./header-minimal.component.html'),
})
export class HeaderMinimalComponent {
  private readonly auth = inject(AuthenticationService);
  private readonly featureToggle = inject(FeatureToggleService);
  private readonly themesHelper = inject(ThemesHelperService);

  private readonly user = this.auth.getUserFromCookie();

  /** Mirrors `user && (user.hasOwnProperty('email') || user.hasOwnProperty('userName'))`. */
  readonly isLoggedIn =
    !!this.user &&
    (Object.prototype.hasOwnProperty.call(this.user, 'email') ||
      Object.prototype.hasOwnProperty.call(this.user, 'userName'));

  readonly useCart = this.featureToggle.check('TENANT_CART');

  /** Mirrors `getCompany('name') || 'Tenant ID: ' + user.tenantId`. */
  readonly companyName: string =
    this.themesHelper.getCompany('name') || `Tenant ID: ${this.user?.tenantId ?? ''}`;

  /** Closes the preview/print window (was `href="javascript:window.close()"`). */
  close(): void {
    window.close();
  }
}
